import React, { useEffect, useRef, useState } from 'react';
import Card from '@/components/ui/Card';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';
import { playerPortalAPI } from '@/services/api';

interface MedicalData {
  injuryStatus: string;
  injuryType: string | null;
  expectedReturnDate: string | null;
}

interface MedicalRevision {
  status: string;
  severity: string;
  notes: string | null;
  isRecurrent: boolean;
  expectedReturnDate: string | null;
  createdAt: string | null;
}

interface MedicalAttachment {
  id: string;
  title: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

interface MedicalReport {
  id: string;
  reportDate: string;
  status: string;
  severity: string;
  injuryType: string | null;
  notes: string | null;
  revisions: MedicalRevision[];
  attachments: MedicalAttachment[];
}

const statusConfig: Record<string, { color: string; bgColor: string; label: string }> = {
  fit: { color: 'text-green-800 dark:text-green-300', bgColor: 'bg-green-100 dark:bg-green-900/40', label: 'Fit' },
  injured: { color: 'text-red-800 dark:text-red-300', bgColor: 'bg-red-100 dark:bg-red-900/40', label: 'Injured' },
  recovering: { color: 'text-amber-800 dark:text-amber-300', bgColor: 'bg-amber-100 dark:bg-amber-900/40', label: 'Recovering' },
  questionable: { color: 'text-yellow-800 dark:text-yellow-300', bgColor: 'bg-yellow-100 dark:bg-yellow-900/40', label: 'Questionable' },
};

const PlayerMedicalPage: React.FC = () => {
  const [data, setData] = useState<MedicalData | null>(null);
  const [reports, setReports] = useState<MedicalReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attachmentError, setAttachmentError] = useState('');
  const objectURLs = useRef<string[]>([]);

  useEffect(() => {
    const urls = objectURLs.current;
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  useEffect(() => {
    // The status card is the primary content, so gate the page on getMedical only.
    // A reports-fetch failure degrades to an empty list rather than blanking the status.
    playerPortalAPI
      .getMedical()
      .then((res: { data: MedicalData }) => setData(res.data))
      .catch(() => setError('Failed to load medical status'))
      .finally(() => setLoading(false));
    playerPortalAPI
      .getMedicalReports()
      .then((res: { data: MedicalReport[] }) => setReports(res.data || []))
      .catch(() => setReports([]));
  }, []);

  const openAttachment = async (id: string) => {
    setAttachmentError('');
    const win = window.open('', '_blank');
    try {
      const blob = await playerPortalAPI.viewMedicalAttachment(id);
      const url = URL.createObjectURL(blob);
      objectURLs.current.push(url);
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      if (win) win.close();
      setAttachmentError('Could not open attachment. Please try again.');
    }
  };

  if (loading) {
    return (
      <DelayedLoading loading>
        <LoadingStatus variant="panel" label="Loading medical status" className="py-12" />
      </DelayedLoading>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md text-sm">
        {error || 'No data available'}
      </div>
    );
  }

  const config = statusConfig[data.injuryStatus] || statusConfig.fit;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Medical Status</h1>

      <Card>
        <div className="flex flex-col items-center text-center py-6 space-y-4">
          <span className={`inline-flex items-center px-6 py-2 rounded-full text-lg font-semibold ${config.bgColor} ${config.color}`}>
            {config.label}
          </span>
          {data.injuryStatus === 'fit' ? (
            <p className="text-gray-700 dark:text-gray-300 text-sm">You are fit and available for selection</p>
          ) : (
            <div className="space-y-2">
              {data.injuryType && (
                <p className="text-gray-700 dark:text-gray-300 text-sm"><span className="font-medium">Injury type:</span> {data.injuryType}</p>
              )}
              {data.expectedReturnDate && (
                <p className="text-gray-700 dark:text-gray-300 text-sm"><span className="font-medium">Expected return:</span> {new Date(data.expectedReturnDate).toLocaleDateString()}</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {attachmentError && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">{attachmentError}</p>
      )}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Medical Reports</h2>
      {reports.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No medical reports on file.</p>
      ) : (
        <div className="space-y-4">
          {reports.map((r) => (
            <Card key={r.id}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                  {r.injuryType || r.status}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{new Date(r.reportDate).toLocaleDateString()}</div>
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-300 space-x-3">
                <span className="capitalize"><span className="font-medium">Status:</span> {r.status}</span>
                <span className="capitalize"><span className="font-medium">Severity:</span> {r.severity}</span>
              </div>
              {r.notes && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{r.notes}</p>}

              {r.attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.attachments.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => openAttachment(a.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                    >
                      <span aria-hidden="true">📎</span>
                      {a.title || a.fileName}
                    </button>
                  ))}
                </div>
              )}

              {r.revisions.length > 0 && (
                <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Updates</div>
                  {r.revisions.map((rev, i) => (
                    <div key={i} className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-gray-500 dark:text-gray-400 tabular-nums mr-2">
                        {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString() : ''}
                      </span>
                      <span className="capitalize font-medium">{rev.status}</span>
                      {rev.expectedReturnDate && <span> · return {new Date(rev.expectedReturnDate).toLocaleDateString()}</span>}
                      {rev.notes && <span> · {rev.notes}</span>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlayerMedicalPage;
